import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, Phone, Mail, MapPin, Clock, ChevronDown, ExternalLink, Send, Loader2, CheckCircle, Calendar, ChevronLeft, ChevronRight, Zap, Droplets, Flame, Hammer, PaintBucket, Home, Wrench, Car, Sun, Lock, Wind, ShieldCheck, TreePine, Plug, CircuitBoard, Lightbulb, Pipette, ThermometerSun, BrickWall, Scissors, Fence, SprayCan, Bug, Waves, HardHat, Paintbrush } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import ChatWidget from "../components/ChatWidget";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FONT_MAP = {
  default: { heading: "'Inter', sans-serif", body: "'Inter', sans-serif", url: null },
  classic: { heading: "'Georgia', serif", body: "'Inter', sans-serif", url: null },
  modern: { heading: "'Outfit', sans-serif", body: "'Inter', sans-serif", url: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&display=swap" },
  bold: { heading: "'Oswald', sans-serif", body: "'Inter', sans-serif", url: "https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap" },
};

const DEFAULT_ORDER = ["hero", "about", "services", "reviews", "gallery", "contact"];

// Trade-to-icon mapping (fallback when service name doesn't match)
const TRADE_ICONS = {
  "Electrician": Zap, "Plumber": Droplets, "Gas Engineer": Flame,
  "General Builder": Hammer, "Carpenter": Hammer, "Painter & Decorator": Paintbrush,
  "Plasterer": BrickWall, "Roofer": Home, "Tiler": BrickWall,
  "Flooring Specialist": BrickWall, "Bathroom Fitter": Droplets, "Kitchen Fitter": Home,
  "Landscaper": TreePine, "Locksmith": Lock, "HVAC Engineer": Wind,
  "Solar Panel Installer": Sun, "EV Charger Installer": Car, "Fire & Security": ShieldCheck,
  "Drainage Specialist": Waves, "Scaffolder": HardHat, "Fencer": Fence,
  "Welder": Flame, "Window Cleaner": SprayCan, "Pest Control": Bug,
};

// Service name keyword-to-icon matching
const SERVICE_KEYWORDS = [
  [/rewir|wiring|cable|socket|switch/i, Plug],
  [/consumer.?unit|fuse.?board|distribution/i, CircuitBoard],
  [/light|lamp|downlight|spotlight/i, Lightbulb],
  [/ev.?charg|vehicle.?charg|charger/i, Car],
  [/solar|panel|pv/i, Sun],
  [/test|inspect|eicr|pat/i, CheckCircle],
  [/boiler|heating|radiator|central.?heat/i, ThermometerSun],
  [/bathroom|shower|bath/i, Droplets],
  [/kitchen|oven|cooker|hob/i, Home],
  [/plumb|pipe|tap|leak/i, Pipette],
  [/gas|flue|ventil/i, Flame],
  [/paint|decorat|wallpaper/i, Paintbrush],
  [/plaster|render|skim/i, BrickWall],
  [/roof|gutter|fascia|soffit/i, Home],
  [/tile|grout|mosaic/i, BrickWall],
  [/floor|carpet|laminate|vinyl/i, BrickWall],
  [/garden|landscap|turf|patio|deck/i, TreePine],
  [/lock|key|door.?entry|access/i, Lock],
  [/air.?con|hvac|ventilat|extract/i, Wind],
  [/alarm|cctv|camera|security|fire.?alarm|smoke/i, ShieldCheck],
  [/drain|unblock|sewer/i, Waves],
  [/scaffold/i, HardHat],
  [/fence|fencing|gate/i, Fence],
  [/weld|fabricat|metal/i, Flame],
  [/clean|window|jet.?wash|pressure/i, SprayCan],
  [/pest|rodent|insect|wasp/i, Bug],
];

function getServiceIcon(serviceName, trades) {
  // 1. Try keyword match on service name
  for (const [regex, Icon] of SERVICE_KEYWORDS) {
    if (regex.test(serviceName)) return Icon;
  }
  // 2. Fall back to the user's primary trade icon
  if (trades && trades.length > 0) {
    return TRADE_ICONS[trades[0]] || Wrench;
  }
  return Wrench;
}

/* Extract just the postal town from a full address */
const extractTown = (address) => {
  if (!address) return "";
  const parts = address.split(/[,\n]+/).map((p) => p.trim()).filter(Boolean);
  const filtered = parts.filter((p) => !/^[A-Z]{1,2}\d[A-Z\d]?\s*\d\s*[A-Z]{2}$/i.test(p));
  if (filtered.length >= 2) return filtered[filtered.length - 1];
  return filtered[0] || address;
};

const StarRating = ({ rating, size = 16 }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.3;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => <Star key={i} size={size} className="fill-yellow-400 text-yellow-400" />)}
      {half && <Star size={size} className="fill-yellow-400 text-yellow-400" />}
    </span>
  );
};

export default function PublicPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const jobRef = searchParams.get("job_ref");
  const [jobPrefilled, setJobPrefilled] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enquiry, setEnquiry] = useState({ name: "", email: "", phone: "", address: "", description: "", preferred_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [bookingUserId, setBookingUserId] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewPaused, setReviewPaused] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [chatConfig, setChatConfig] = useState(null);
  const contactRef = useRef(null);

  // Pre-fill the enquiry from a directory job when the homeowner arrives via a
  // job-match email link (?job_ref=...). Non-PII: only their own description.
  useEffect(() => {
    if (!jobRef) return;
    axios.get(`${API_URL}/api/directory/job-context/${jobRef}`)
      .then((r) => {
        if (r.data?.description) {
          setEnquiry((prev) => ({ ...prev, description: prev.description || r.data.description }));
          setJobPrefilled(true);
          setTimeout(() => contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
        }
      })
      .catch(() => {});
  }, [jobRef]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pages/public/${slug}`);
        setData(res.data);
        if (res.data.booking_enabled && res.data.user_id) {
          setBookingEnabled(true);
          setBookingUserId(res.data.user_id);
          try {
            const infoRes = await axios.get(`${API_URL}/api/booking/public/${res.data.user_id}/info`);
            setBookingInfo(infoRes.data);
          } catch {}
        }
        // Fetch chat assistant config
        try {
          const chatRes = await axios.get(`${API_URL}/api/chat/public/embed/${slug}?check_my_page=true`);
          setChatConfig(chatRes.data);
        } catch {}
      } catch {
        setError("Page not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  // Load Google Font if needed
  useEffect(() => {
    if (!data) return;
    const fontPairing = data.page?.theme?.font_pairing || "default";
    const font = FONT_MAP[fontPairing] || FONT_MAP.default;
    if (font.url) {
      const linkId = "qs-page-font";
      if (!document.getElementById(linkId)) {
        const link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        link.href = font.url;
        document.head.appendChild(link);
      }
    }
  }, [data]);

  // Schema.org + OG tags
  useEffect(() => {
    if (!data) return;
    const { page, company, google_reviews } = data;
    const { sections } = page;
    const pageUrl = `${API_URL}/p/${slug}`;
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": company.name || page.seo?.title || "Tradesperson",
      "url": pageUrl,
      ...(company.logo && { "logo": company.logo }),
      ...(page.seo?.description && { "description": page.seo.description }),
      ...(sections.hero?.background_image && { "image": sections.hero.background_image }),
      ...(company.phone && { "telephone": company.phone }),
      ...(company.email && { "email": company.email }),
      ...(company.address && {
        "address": { "@type": "PostalAddress", "addressLocality": extractTown(company.address) }
      }),
      ...(google_reviews && google_reviews.rating > 0 && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": google_reviews.rating,
          "reviewCount": google_reviews.review_count,
          "bestRating": 5,
          "worstRating": 1,
        }
      }),
      ...(sections.services?.enabled && sections.services.items?.length > 0 && {
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Services",
          "itemListElement": sections.services.items.map((s) => ({
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": s.name, ...(s.description && { "description": s.description }) }
          })),
        }
      }),
    };

    const title = page.seo?.title || `${company.name} | QuoteSorted`;
    const desc = page.seo?.description || `${company.name} — professional tradesperson. Request a free quote.`;
    const ogImage = sections.hero?.background_image || company.logo || "";
    document.title = title;

    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", pageUrl);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", "QuoteSorted");
    setMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);

    const scriptId = "qs-schema-jsonld";
    let existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { const el = document.getElementById(scriptId); if (el) el.remove(); };
  }, [data, slug]);

  // Auto-rotate reviews every 5 seconds, pause on hover
  useEffect(() => {
    if (!data?.google_reviews?.reviews?.length || data.google_reviews.reviews.length <= 1 || reviewPaused) return;
    const timer = setInterval(() => {
      setReviewIndex((prev) => (prev === data.google_reviews.reviews.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [data, reviewPaused]);

  const scrollToContact = () => contactRef.current?.scrollIntoView({ behavior: "smooth" });

  const fetchSlots = async (dateStr) => {
    if (!bookingUserId) return;
    setLoadingSlots(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    try {
      const res = await axios.get(`${API_URL}/api/booking/public/${bookingUserId}/availability?date=${dateStr}`);
      const slots = (res.data.slots || []).filter((s) => s.available).map((s) => s.time);
      setAvailableSlots(slots);
    } catch {
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setEnquiry((prev) => ({ ...prev, preferred_date: dateStr }));
    fetchSlots(dateStr);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setEnquiry((prev) => ({ ...prev, preferred_date: `${selectedDate} at ${slot}` }));
  };

  const isDayAvailable = (date) => {
    if (!bookingInfo?.available_days) return true;
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[date.getDay()];
    const dayConfig = bookingInfo.available_days[dayName];
    if (!dayConfig?.enabled) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    const maxDays = bookingInfo.max_advance_days || 30;
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxDays);
    return date <= maxDate;
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    if (!enquiry.name) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/pages/public/${slug}/enquiry`, enquiry);
      // Also create a booking if booking is enabled and a slot was selected
      if (bookingEnabled && bookingUserId && selectedDate && selectedSlot) {
        try {
          await axios.post(`${API_URL}/api/booking/public/${bookingUserId}/book`, {
            customer_name: enquiry.name,
            customer_email: enquiry.email,
            customer_phone: enquiry.phone,
            customer_address: enquiry.address,
            date: selectedDate,
            time: selectedSlot,
            job_description: enquiry.description || "Quote request via My Page",
            source: "page_enquiry",
          });
        } catch (bookErr) {
          // Enquiry already saved — log so a booking/GCal failure is visible.
          console.error("Booking creation failed:", bookErr.response?.data || bookErr);
        }
      }
      setSubmitted(true);
    } catch {
      alert("Failed to send. Please try calling instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h1>
        <p className="text-gray-500">This tradesperson page doesn't exist or isn't published yet.</p>
      </div>
    </div>
  );

  const { page, company, google_reviews } = data;
  const { sections, theme } = page;
  const primary = theme?.primary_color || "#3B82F6";
  const accent = theme?.accent_color || "#F59E0B";
  const tpl = theme?.template || "classic";
  const isDark = theme?.mode === "dark";
  const fontPairing = theme?.font_pairing || "default";
  const fonts = FONT_MAP[fontPairing] || FONT_MAP.default;
  const sectionOrder = page.section_order || DEFAULT_ORDER;

  // Template-specific styles
  const bg = isDark ? "#0f172a" : "#ffffff";
  const bgAlt = isDark ? "#1e293b" : "#f9fafb";
  const textColor = isDark ? "#f1f5f9" : "#111827";
  const textMuted = isDark ? "#94a3b8" : "#6b7280";
  const borderColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";

  // Template-specific hero alignment
  const heroAlign = tpl === "minimal" ? "text-left" : "text-center";
  const heroMax = tpl === "minimal" ? "max-w-2xl" : "max-w-3xl mx-auto";

  // Section renderers
  const renderHero = () => sections.hero?.enabled && (
    <section
      key="hero"
      className={`relative flex items-center ${tpl === "minimal" ? "justify-start" : "justify-center"} min-h-[70vh] sm:min-h-[80vh] px-4`}
      style={{
        backgroundImage: sections.hero.background_image ? `url(${sections.hero.background_image})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: sections.hero.background_image && sections.hero.parallax ? "fixed" : undefined,
        backgroundColor: sections.hero.background_image ? undefined : (isDark ? "#0f172a" : "#1e293b"),
      }}
      data-testid="page-hero"
    >
      {sections.hero.background_image && (
        <div className="absolute inset-0" style={{ background: tpl === "bold" ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.55)" }} />
      )}
      {!sections.hero.background_image && (
        <div className="absolute inset-0" style={{ background: tpl === "bold" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.2)" }} />
      )}
      <div className={`relative z-10 ${heroAlign} ${heroMax} ${tpl === "minimal" ? "ml-4 sm:ml-12 lg:ml-24" : ""}`}>
        {google_reviews && sections.hero.show_rating && (
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <StarRating rating={google_reviews.rating} size={18} />
            <span className="text-white font-semibold text-sm">{google_reviews.rating}/5</span>
            <span className="text-white/70 text-sm">({google_reviews.review_count} reviews)</span>
          </div>
        )}
        <h1
          className={`font-bold text-white mb-4 leading-tight ${tpl === "bold" ? "text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight" : "text-4xl sm:text-5xl lg:text-6xl"}`}
          style={{ fontFamily: fonts.heading }}
        >
          {sections.hero.headline}
        </h1>
        <p className="text-lg sm:text-xl text-white/80 mb-8" style={{ fontFamily: fonts.body }}>
          {sections.hero.subtitle}
        </p>
        <div className={`flex ${tpl === "minimal" ? "" : "justify-center"} flex-col sm:flex-row items-${tpl === "minimal" ? "start" : "center"} gap-4`}>
          <Button
            size="lg"
            onClick={scrollToContact}
            className={`text-white px-8 py-3 text-base font-semibold shadow-lg ${tpl === "bold" ? "rounded-none uppercase tracking-wider" : "rounded-lg"}`}
            style={{ backgroundColor: primary, fontFamily: fonts.heading }}
            data-testid="hero-cta"
          >
            {sections.hero.cta_text}
          </Button>
          {company.phone && sections.hero.show_phone && (
            <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-white/90 hover:text-white font-medium text-base transition">
              <Phone size={18} /> {company.phone}
            </a>
          )}
        </div>
      </div>
    </section>
  );

  const renderAbout = () => sections.about?.enabled && (
    <section key="about" id="about" className="py-16 sm:py-24 px-4" style={{ backgroundColor: bg }} data-testid="page-about">
      <div className={`max-w-5xl mx-auto ${tpl === "minimal" ? "" : ""}`}>
        <h2
          className={`text-2xl sm:text-3xl font-bold mb-8 ${tpl === "minimal" ? "text-left" : "text-center"}`}
          style={{ color: textColor, fontFamily: fonts.heading }}
        >
          {sections.about.heading}
        </h2>
        <div className={`grid ${sections.about.image ? "md:grid-cols-2 gap-12 items-center" : "max-w-3xl mx-auto"}`}>
          <div>
            {sections.about.body ? (
              <p className="leading-relaxed whitespace-pre-line" style={{ color: textMuted, fontFamily: fonts.body }}>{sections.about.body}</p>
            ) : (
              <p className="italic" style={{ color: textMuted }}>About section content coming soon.</p>
            )}
          </div>
          {sections.about.image && (
            <div className={`overflow-hidden ${sections.about.image.endsWith('.png') ? '' : `shadow-lg ${tpl === "bold" ? "rounded-none" : "rounded-xl"}`}`}>
              <img src={sections.about.image} alt="About" className={`w-full h-64 sm:h-80 ${sections.about.image.endsWith('.png') ? 'object-contain' : 'object-cover'}`} />
            </div>
          )}
        </div>
        {sections.about.highlights?.length > 0 && (
          <div className={`flex flex-wrap ${tpl === "minimal" ? "justify-start" : "justify-center"} gap-3 mt-10`}>
            {sections.about.highlights.map((h, i) => (
              <span key={i} className={`px-4 py-2 text-sm font-medium border ${tpl === "bold" ? "rounded-none uppercase tracking-wide text-xs" : "rounded-full"}`} style={{ borderColor: primary, color: primary }}>
                {h}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );

  const renderServices = () => sections.services?.enabled && sections.services.items?.length > 0 && (
    <section key="services" id="services" className="py-16 sm:py-24 px-4" style={{ backgroundColor: bgAlt }} data-testid="page-services">
      <div className="max-w-5xl mx-auto">
        <h2
          className={`text-2xl sm:text-3xl font-bold mb-10 ${tpl === "minimal" ? "text-left" : "text-center"} ${tpl === "bold" ? "uppercase tracking-wide" : ""}`}
          style={{ color: textColor, fontFamily: fonts.heading }}
        >
          {sections.services.heading}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.services.items.map((service, i) => {
            const ServiceIcon = getServiceIcon(service.name, company.trades);
            return (
            <div
              key={i}
              className={`p-6 shadow-sm border hover:shadow-md transition ${tpl === "bold" ? "rounded-none border-l-4" : "rounded-xl border-gray-100"}`}
              style={{
                backgroundColor: isDark ? "#1e293b" : "#ffffff",
                borderColor: tpl === "bold" ? primary : borderColor,
              }}
            >
              <div className={`w-10 h-10 flex items-center justify-center mb-4 ${tpl === "bold" ? "rounded-none" : "rounded-lg"}`} style={{ backgroundColor: `${primary}15` }}>
                <ServiceIcon size={20} style={{ color: primary }} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: textColor, fontFamily: fonts.heading }}>{service.name}</h3>
              {service.description && <p className="text-sm" style={{ color: textMuted, fontFamily: fonts.body }}>{service.description}</p>}
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );

  const renderReviews = () => sections.reviews?.enabled && google_reviews && (
    <section key="reviews" id="reviews" className="py-16 sm:py-24 px-4" style={{ backgroundColor: bg }} data-testid="page-reviews">
      <div className={`max-w-5xl mx-auto ${tpl === "minimal" ? "text-left" : "text-center"}`}>
        <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: textColor, fontFamily: fonts.heading }}>{sections.reviews.heading}</h2>
        <div className={`flex items-center ${tpl === "minimal" ? "justify-start" : "justify-center"} gap-3 mb-10`}>
          <StarRating rating={google_reviews.rating} size={24} />
          <span className="text-2xl font-bold" style={{ color: textColor }}>{google_reviews.rating}/5</span>
          <span style={{ color: textMuted }}>({google_reviews.review_count} reviews on Google)</span>
        </div>

        {/* Review Slider */}
        {google_reviews.reviews && google_reviews.reviews.length > 0 && (
          <div
            className="relative max-w-3xl mx-auto mb-10"
            onMouseEnter={() => setReviewPaused(true)}
            onMouseLeave={() => setReviewPaused(false)}
          >
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${reviewIndex * 100}%)` }}
              >
                {google_reviews.reviews.map((review, i) => (
                  <div key={i} className="w-full flex-shrink-0 px-4">
                    <div
                      className={`p-6 sm:p-8 shadow-sm border ${tpl === "bold" ? "rounded-none" : "rounded-2xl"}`}
                      style={{ backgroundColor: isDark ? "#1e293b" : "#ffffff", borderColor }}
                    >
                      <div className="flex items-center gap-1 mb-3 justify-center">
                        {[...Array(5)].map((_, s) => (
                          <Star key={s} size={18} className={s < review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} />
                        ))}
                      </div>
                      <p className="text-base sm:text-lg leading-relaxed mb-6" style={{ color: textColor, fontFamily: fonts.body, fontStyle: "italic" }}>
                        "{review.text}"
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        {review.photo && (
                          <img src={review.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                        )}
                        <div>
                          <p className="font-semibold text-sm" style={{ color: textColor }}>{review.author}</p>
                          {review.time && <p className="text-xs" style={{ color: textMuted }}>{review.time}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation arrows */}
            {google_reviews.reviews.length > 1 && (
              <>
                <button
                  onClick={() => setReviewIndex((prev) => (prev === 0 ? google_reviews.reviews.length - 1 : prev - 1))}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 w-10 h-10 flex items-center justify-center rounded-full shadow-md transition hover:scale-110"
                  style={{ backgroundColor: isDark ? "#334155" : "#ffffff", color: textColor }}
                  data-testid="review-prev"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setReviewIndex((prev) => (prev === google_reviews.reviews.length - 1 ? 0 : prev + 1))}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 w-10 h-10 flex items-center justify-center rounded-full shadow-md transition hover:scale-110"
                  style={{ backgroundColor: isDark ? "#334155" : "#ffffff", color: textColor }}
                  data-testid="review-next"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            {/* Dots */}
            {google_reviews.reviews.length > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {google_reviews.reviews.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setReviewIndex(i)}
                    className="w-2.5 h-2.5 rounded-full transition-all"
                    style={{ backgroundColor: i === reviewIndex ? primary : (isDark ? "#475569" : "#d1d5db") }}
                    data-testid={`review-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {sections.reviews.show_leave_review_cta && google_reviews.review_url && (
          <a
            href={google_reviews.review_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-6 py-3 text-white font-medium transition hover:opacity-90 ${tpl === "bold" ? "rounded-none uppercase tracking-wider text-sm" : "rounded-lg"}`}
            style={{ backgroundColor: primary }}
            data-testid="page-leave-review"
          >
            Leave Us a Google Review <ExternalLink size={16} />
          </a>
        )}
      </div>
    </section>
  );

  const renderGallery = () => {
    const gallery = sections.gallery;
    if (!gallery?.enabled || !gallery.images?.length) return null;
    // Normalise images — handle both {url, caption} objects and plain URL strings
    const images = gallery.images.map((img) =>
      typeof img === "string" ? { url: img, caption: "" } : img
    ).filter((img) => img && img.url);
    if (!images.length) return null;
    return (
      <section key="gallery" className="py-16 sm:py-24 px-4" style={{ backgroundColor: bg }} data-testid="page-gallery">
        <div className="max-w-5xl mx-auto">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-10 ${tpl === "minimal" ? "text-left" : "text-center"}`} style={{ color: textColor, fontFamily: fonts.heading }}>{gallery.heading || "Gallery"}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((img, i) => (
              <div
                key={i}
                className={`overflow-hidden shadow-sm aspect-square cursor-pointer ${tpl === "bold" ? "rounded-none" : "rounded-xl"}`}
                onClick={() => setLightboxImg(img)}
                data-testid={`gallery-image-${i}`}
              >
                <img src={img.url} alt={img.caption || `Gallery ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderContact = () => sections.contact?.enabled && (
    <section key="contact" id="contact" ref={contactRef} className="py-16 sm:py-24 px-4" style={{ backgroundColor: bgAlt }} data-testid="page-contact">
      <div className="max-w-5xl mx-auto">
        <h2 className={`text-2xl sm:text-3xl font-bold mb-10 ${tpl === "minimal" ? "text-left" : "text-center"}`} style={{ color: textColor, fontFamily: fonts.heading }}>{sections.contact.heading}</h2>
        <div className="grid md:grid-cols-2 gap-10">
          <div className="space-y-6">
            <p style={{ color: textMuted, fontFamily: fonts.body }}>Get in touch for a free, no-obligation quote. We'd love to hear from you.</p>
            {company.phone && sections.contact.show_phone && (
              <a href={`tel:${company.phone}`} className="flex items-center gap-3 hover:opacity-80 transition group" style={{ color: textColor }}>
                <div className={`w-10 h-10 flex items-center justify-center ${tpl === "bold" ? "rounded-none" : "rounded-lg"}`} style={{ backgroundColor: `${primary}15` }}>
                  <Phone size={18} style={{ color: primary }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: textMuted }}>Call us</p>
                  <p className="font-semibold">{company.phone}</p>
                </div>
              </a>
            )}
            {company.email && sections.contact.show_email && (
              <a href={`mailto:${company.email}`} className="flex items-center gap-3 hover:opacity-80 transition" style={{ color: textColor }}>
                <div className={`w-10 h-10 flex items-center justify-center ${tpl === "bold" ? "rounded-none" : "rounded-lg"}`} style={{ backgroundColor: `${primary}15` }}>
                  <Mail size={18} style={{ color: primary }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: textMuted }}>Email us</p>
                  <p className="font-semibold">{company.email}</p>
                </div>
              </a>
            )}
            {company.address && (
              <div className="flex items-center gap-3" style={{ color: textColor }}>
                <div className={`w-10 h-10 flex items-center justify-center ${tpl === "bold" ? "rounded-none" : "rounded-lg"}`} style={{ backgroundColor: `${primary}15` }}>
                  <MapPin size={18} style={{ color: primary }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: textMuted }}>Location</p>
                  <p className="font-semibold">{extractTown(company.address)}</p>
                </div>
              </div>
            )}
          </div>

          {sections.contact.show_form && (
            <div className={`shadow-sm border p-6 sm:p-8 ${tpl === "bold" ? "rounded-none" : "rounded-xl"}`} style={{ backgroundColor: isDark ? "#1e293b" : "#ffffff", borderColor }}>
              {submitted ? (
                <div className="text-center py-8" data-testid="enquiry-success">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
                  <h3 className="text-xl font-bold mb-2" style={{ color: textColor }}>Enquiry Sent!</h3>
                  <p style={{ color: textMuted }}>We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitEnquiry} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: textColor }}>Your name *</label>
                    <Input data-testid="enquiry-name" required value={enquiry.name} onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })} placeholder="John Smith" style={{ color: textColor, backgroundColor: isDark ? "#1e293b" : "#fff", borderColor }} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block" style={{ color: textColor }}>Phone</label>
                      <Input data-testid="enquiry-phone" value={enquiry.phone} onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })} placeholder="07123 456789" style={{ color: textColor, backgroundColor: isDark ? "#1e293b" : "#fff", borderColor }} />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block" style={{ color: textColor }}>Email</label>
                      <Input data-testid="enquiry-email" type="email" value={enquiry.email} onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })} placeholder="john@email.com" style={{ color: textColor, backgroundColor: isDark ? "#1e293b" : "#fff", borderColor }} />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: textColor }}>Address *</label>
                    <Textarea data-testid="enquiry-address" required rows={2} value={enquiry.address} onChange={(e) => setEnquiry({ ...enquiry, address: e.target.value })} placeholder="123 High Street, Northallerton, DL6 1AA" style={{ color: textColor, backgroundColor: isDark ? "#1e293b" : "#fff", borderColor }} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: textColor }}>
                      {bookingEnabled ? "Select a date & time" : "Preferred date"}
                    </label>
                    {bookingEnabled && bookingInfo ? (() => {
                      const year = calendarMonth.getFullYear();
                      const month = calendarMonth.getMonth();
                      const firstDay = new Date(year, month, 1).getDay();
                      const daysInMonth = new Date(year, month + 1, 0).getDate();
                      const dayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
                      const cells = [];
                      for (let i = 0; i < firstDay; i++) cells.push(null);
                      for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                      const monthName = calendarMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
                      return (
                        <div className="space-y-3" data-testid="booking-calendar">
                          <div className="border rounded-lg p-3" style={{ borderColor, backgroundColor: isDark ? "#0f172a" : "#f8fafc" }}>
                            <div className="flex items-center justify-between mb-2">
                              <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-1 rounded hover:bg-gray-200/50"><ChevronLeft size={16} style={{ color: textMuted }} /></button>
                              <span className="text-sm font-semibold" style={{ color: textColor }}>{monthName}</span>
                              <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-1 rounded hover:bg-gray-200/50"><ChevronRight size={16} style={{ color: textMuted }} /></button>
                            </div>
                            <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
                              {dayLabels.map((d) => <div key={d} className="text-[10px] font-medium py-0.5" style={{ color: textMuted }}>{d}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-0.5 text-center">
                              {cells.map((day, i) => {
                                if (!day) return <div key={`e-${i}`} />;
                                const dateObj = new Date(year, month, day);
                                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                const available = isDayAvailable(dateObj);
                                const isSelected = selectedDate === dateStr;
                                return (
                                  <button
                                    key={day}
                                    type="button"
                                    disabled={!available}
                                    onClick={() => available && handleDateSelect(dateStr)}
                                    className={`text-xs py-1.5 rounded-md transition-all ${
                                      isSelected ? "text-white font-bold" : available ? "hover:opacity-80 font-medium" : "opacity-25 cursor-not-allowed"
                                    }`}
                                    style={{
                                      backgroundColor: isSelected ? primary : "transparent",
                                      color: isSelected ? "#fff" : textColor,
                                    }}
                                    data-testid={`cal-day-${day}`}
                                  >
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          {selectedDate && (
                            <div data-testid="time-slots">
                              <p className="text-xs font-medium mb-2" style={{ color: textMuted }}>
                                Available times for {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                              </p>
                              {loadingSlots ? (
                                <div className="flex items-center gap-2 py-2"><Loader2 size={14} className="animate-spin" style={{ color: textMuted }} /><span className="text-xs" style={{ color: textMuted }}>Loading slots...</span></div>
                              ) : availableSlots.length > 0 ? (
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                  {availableSlots.map((slot) => (
                                    <button
                                      key={slot}
                                      type="button"
                                      onClick={() => handleSlotSelect(slot)}
                                      className={`text-xs py-2 px-1 rounded-md border transition-all font-medium ${
                                        selectedSlot === slot ? "text-white border-transparent" : "hover:opacity-80"
                                      }`}
                                      style={{
                                        backgroundColor: selectedSlot === slot ? primary : "transparent",
                                        borderColor: selectedSlot === slot ? primary : borderColor,
                                        color: selectedSlot === slot ? "#fff" : textColor,
                                      }}
                                      data-testid={`slot-${slot.replace(":", "")}`}
                                    >
                                      {slot}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs py-2" style={{ color: textMuted }}>No available slots for this date.</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      <Input data-testid="enquiry-date" type="date" value={enquiry.preferred_date} onChange={(e) => setEnquiry({ ...enquiry, preferred_date: e.target.value })} style={{ color: textColor, backgroundColor: isDark ? "#1e293b" : "#fff", borderColor }} />
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: textColor }}>What do you need doing?</label>
                    {jobPrefilled && (
                      <p className="text-xs mb-2 rounded-md px-2.5 py-1.5" style={{ color: primary, backgroundColor: isDark ? "#0f172a" : "#eff6ff" }} data-testid="enquiry-prefilled-note">
                        We've pre-filled your job details — edit anything before sending.
                      </p>
                    )}
                    <Textarea data-testid="enquiry-description" rows={4} value={enquiry.description} onChange={(e) => setEnquiry({ ...enquiry, description: e.target.value })} placeholder="Describe the work you need..." style={{ color: textColor, backgroundColor: isDark ? "#1e293b" : "#fff", borderColor }} />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className={`w-full text-white font-semibold py-3 ${tpl === "bold" ? "rounded-none uppercase tracking-wider" : ""}`}
                    style={{ backgroundColor: primary }}
                    data-testid="enquiry-submit"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                    Send Enquiry
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );

  const sectionRenderers = {
    hero: renderHero,
    about: renderAbout,
    services: renderServices,
    reviews: renderReviews,
    gallery: renderGallery,
    contact: renderContact,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg, "--primary": primary, "--accent": accent }}>
      {/* Sticky Nav */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-sm px-4 py-3"
        style={{ backgroundColor: isDark ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.95)", borderBottom: `1px solid ${borderColor}` }}
        data-testid="page-nav"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {company.logo && <img src={company.logo} alt="" className="h-8 w-8 rounded object-contain" />}
            <span className="font-bold text-sm sm:text-base" style={{ color: textColor, fontFamily: fonts.heading }}>{company.name}</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm" style={{ color: textMuted }}>
            {sections.about?.enabled && <a href="#about" className="hover:opacity-80 transition">About</a>}
            {sections.services?.enabled && <a href="#services" className="hover:opacity-80 transition">Services</a>}
            {sections.reviews?.enabled && google_reviews && <a href="#reviews" className="hover:opacity-80 transition">Reviews</a>}
            {sections.contact?.enabled && <a href="#contact" className="hover:opacity-80 transition">Contact</a>}
          </div>
          <div className="flex items-center gap-3">
            {company.phone && (
              <a href={`tel:${company.phone}`} className="text-sm font-medium flex items-center gap-1.5 hover:opacity-80" style={{ color: primary }}>
                <Phone size={14} /> <span className="hidden sm:inline">{company.phone}</span>
              </a>
            )}
            <Button
              size="sm"
              onClick={scrollToContact}
              style={{ backgroundColor: primary, fontFamily: fonts.heading }}
              className={`text-white text-xs sm:text-sm ${tpl === "bold" ? "rounded-none uppercase tracking-wider" : ""}`}
            >
              Free Quote
            </Button>
          </div>
        </div>
      </nav>

      {/* Sections in order */}
      {sectionOrder.map((key) => sectionRenderers[key]?.())}

      {/* Footer */}
      <footer className="py-8 px-4" style={{ borderTop: `1px solid ${borderColor}` }} data-testid="page-footer">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" style={{ color: textMuted }}>
          <p>&copy; {new Date().getFullYear()} {company.name}. All rights reserved.</p>
          <a href="https://quotesorted.uk" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition">
            Powered by QuoteSorted
          </a>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxImg && lightboxImg.url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
          data-testid="gallery-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImg.url}
              alt={lightboxImg.caption || "Gallery image"}
              className="w-full max-h-[85vh] object-contain rounded-lg"
            />
            {lightboxImg.caption && (
              <p className="text-white text-center text-sm mt-3 opacity-80">{lightboxImg.caption}</p>
            )}
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-800 shadow-lg hover:bg-gray-100 transition"
              data-testid="lightbox-close"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      {/* Chat Widget */}
      {chatConfig && (
        <ChatWidget
          slug={slug}
          companyName={chatConfig.company_name}
          widgetColor={chatConfig.widget_color}
          greeting={chatConfig.greeting}
          assistantName={chatConfig.assistant_name}
        />
      )}
    </div>
  );
}
